import React, { useState } from "react";
import { useCart } from "../context/CartContext";
import { Trash2, Plus, Minus, ArrowRight, ArrowLeft, CheckCircle } from "lucide-react";
import { Link } from "react-router-dom";

function Checkout() {
  const { cartItems, removeFromCart, updateQuantity, clearCart } = useCart();
  const [shippingAddress, setShippingAddress] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("mpesa");
  const [step, setStep] = useState(1); // 1 = Cart, 2 = Shipping, 3 = Payment, 4 = Success
  const [isProcessing, setIsProcessing] = useState(false);

  const subtotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0,
  );

  const handleNextStep = () => {
    if (step === 2 && !shippingAddress.trim()) {
      alert("Please enter a shipping address to continue.");
      return;
    }
    setStep((prev) => prev + 1);
  };

  const handlePrevStep = () => {
    setStep((prev) => prev - 1);
  };

  const handleCheckout = () => {
    if (!shippingAddress.trim() || (paymentMethod === "mpesa" && !phoneNumber.trim())) {
      alert("Please fill in all required payment & shipping details.");
      return;
    }
    
    setIsProcessing(true);
    // Simulate API call for checkout process
    setTimeout(() => {
      setIsProcessing(false);
      setStep(4); // Success step
      clearCart();
    }, 2000);
  };

  if (cartItems.length === 0 && step !== 4) {
    return (
      <div className="max-w-4xl mx-auto mt-24 p-8 text-center bg-gray-50 rounded-3xl border border-gray-100 shadow-sm">
        <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Your Cart is Empty</h2>
        <p className="text-gray-500 mb-8 max-w-md mx-auto">Looks like you haven't added anything to your cart yet. Let's find you some great items!</p>
        <Link to="/shop">
          <button className="bg-yellow-500 hover:bg-yellow-400 text-black px-8 py-3 rounded-full font-bold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5">
            Start Shopping
          </button>
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-5xl mx-auto mt-24 p-4 md:p-6 mb-20 min-h-[60vh]">
      {/* Stepper Header */}
      {step < 4 && (
        <div className="mb-12">
          <div className="flex items-center justify-between max-w-2xl mx-auto relative">
            <div className="absolute top-1/2 left-0 w-full h-1 bg-gray-100 -translate-y-1/2 z-0 rounded-full"></div>
            <div 
              className="absolute top-1/2 left-0 h-1 bg-yellow-400 -translate-y-1/2 z-0 rounded-full transition-all duration-500"
              style={{ width: `${((step - 1) / 2) * 100}%` }}
            ></div>
            
            {[
              { num: 1, label: "Your Cart" },
              { num: 2, label: "Shipping" },
              { num: 3, label: "Payment" },
            ].map((s) => (
              <div key={s.num} className="relative z-10 flex flex-col items-center">
                <div 
                  className={`w-12 h-12 flex items-center justify-center rounded-full font-bold text-lg transition-all duration-300 ${
                    step >= s.num 
                      ? "bg-yellow-500 text-black shadow-md border-4 border-white" 
                      : "bg-gray-100 text-gray-400 border-4 border-white"
                  }`}
                >
                  {step > s.num ? <CheckCircle size={20} /> : s.num}
                </div>
                <span className={`text-sm font-semibold mt-2 absolute -bottom-7 w-24 text-center ${
                  step >= s.num ? "text-gray-900" : "text-gray-400"
                }`}>
                  {s.label}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Main Content Area - Glassmorphism Card */}
      <div className="bg-white rounded-3xl shadow-[0_8px_30px_rgb(0,0,0,0.04)] border border-gray-100 p-6 md:p-10 transition-all duration-300 relative overflow-hidden">
        
        {/* Step 1: Cart Items */}
        {step === 1 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-100">Review Your Order</h3>
            
            <div className="space-y-6">
              {cartItems.map((item) => (
                <div
                  key={item.id}
                  className="flex flex-col sm:flex-row bg-gray-50/50 rounded-2xl border border-gray-100 overflow-hidden hover:bg-gray-50 transition-colors gap-4"
                >
                  <img
                    src={item.image}
                    alt={item.name}
                    className="w-full sm:w-36 h-40 object-cover"
                  />
                  <div className="flex flex-col justify-between p-5 flex-grow">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-xl font-bold text-gray-900">{item.name}</h3>
                        <p className="text-gray-500 text-sm mt-1">Ref: PK-{item.id.substring(0, 6).toUpperCase()}</p>
                      </div>
                      <p className="text-xl font-extrabold text-black">
                        Ksh {item.price.toLocaleString()}
                      </p>
                    </div>

                    <div className="flex items-center justify-between mt-6">
                      <div className="flex items-center bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
                        <button
                          onClick={() => updateQuantity(item.id, -1)}
                          disabled={item.quantity <= 1}
                          className="px-4 py-2.5 text-gray-500 hover:bg-yellow-50 hover:text-yellow-600 disabled:opacity-30 disabled:hover:bg-transparent transition-colors"
                        >
                          <Minus size={18} />
                        </button>
                        <span className="w-12 text-center font-bold text-gray-900 bg-gray-50/50 py-2.5">
                          {item.quantity}
                        </span>
                        <button
                          onClick={() => updateQuantity(item.id, 1)}
                          className="px-4 py-2.5 text-gray-500 hover:bg-yellow-50 hover:text-yellow-600 transition-colors"
                        >
                          <Plus size={18} />
                        </button>
                      </div>

                      <button
                        onClick={() => removeFromCart(item.id)}
                        className="flex items-center gap-2 text-gray-400 hover:text-red-500 hover:bg-red-50 px-4 py-2.5 rounded-xl transition-all"
                      >
                        <Trash2 size={20} /> <span className="hidden sm:inline font-medium">Remove</span>
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-6">
              <div className="text-center sm:text-left w-full sm:w-auto">
                <p className="text-gray-500 font-medium mb-1">Estimated Subtotal</p>
                <p className="text-3xl font-extrabold text-black">Ksh {subtotal.toLocaleString()}</p>
              </div>
              <button
                onClick={handleNextStep}
                className="w-full sm:w-auto bg-gray-900 hover:bg-black text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-colors shadow-lg shadow-gray-200"
              >
                Continue to Shipping <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Shipping */}
        {step === 2 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-100">Delivery Details</h3>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Full Shipping Address
                </label>
                <textarea
                  placeholder="Street name, apartment, estate, city..."
                  value={shippingAddress}
                  onChange={(e) => setShippingAddress(e.target.value)}
                  className="w-full p-4 bg-gray-50 border border-gray-200 rounded-2xl text-gray-900 focus:outline-none focus:bg-white focus:ring-2 focus:ring-yellow-400 focus:border-transparent transition-all min-h-[120px] resize-none"
                />
              </div>

              <div className="bg-yellow-50/50 p-6 rounded-2xl border border-yellow-100">
                <h4 className="font-bold text-yellow-800 mb-2">Delivery Note</h4>
                <p className="text-sm text-yellow-700 leading-relaxed">
                  Deliveries within Nanyuki town are mostly free and happen same-day. Nationwide deliveries take 1-3 business days via Fargo Courier or G4S.
                </p>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={handlePrevStep}
                className="w-full sm:w-auto px-6 py-4 text-gray-500 hover:text-gray-900 font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                <ArrowLeft size={20} /> Back to Cart
              </button>
              <button
                onClick={handleNextStep}
                disabled={!shippingAddress.trim()}
                className="w-full sm:w-auto bg-gray-900 hover:bg-black text-white px-8 py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg shadow-gray-200"
              >
                Continue to Payment <ArrowRight size={20} />
              </button>
            </div>
          </div>
        )}

        {/* Step 3: Payment */}
        {step === 3 && (
          <div className="animate-in fade-in slide-in-from-right-4 duration-500 max-w-2xl mx-auto">
            <h3 className="text-2xl font-bold text-gray-900 mb-8 pb-4 border-b border-gray-100">Secure Payment</h3>
            
            <div className="space-y-6">
              <div className="space-y-3">
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Select Payment Method
                </label>
                
                <div 
                  onClick={() => setPaymentMethod("mpesa")}
                  className={`p-5 rounded-2xl border-2 cursor-pointer transition-all flex items-center justify-between ${
                    paymentMethod === "mpesa" ? "border-green-500 bg-green-50/30" : "border-gray-100 bg-gray-50 hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === "mpesa" ? "border-green-500" : "border-gray-300"
                    }`}>
                      {paymentMethod === "mpesa" && <div className="w-3 h-3 rounded-full bg-green-500"></div>}
                    </div>
                    <span className="font-bold text-gray-900 text-lg">M-Pesa (Recommended)</span>
                  </div>
                  <span className="text-xl">📱</span>
                </div>

                {paymentMethod === "mpesa" && (
                  <div className="pl-[3.25rem] pr-5 pb-5 pt-2 animate-in slide-in-from-top-2 duration-300">
                    <input
                      type="tel"
                      placeholder="e.g. 0712345678"
                      value={phoneNumber}
                      onChange={(e) => setPhoneNumber(e.target.value)}
                      className="w-full p-4 bg-white border border-gray-200 rounded-xl text-gray-900 focus:outline-none focus:ring-2 focus:ring-green-400 focus:border-transparent transition-all"
                    />
                    <p className="text-sm text-gray-500 mt-2">Enter the Safaricom number to receive the payment prompt.</p>
                  </div>
                )}

                <div 
                  onClick={() => setPaymentMethod("card")}
                  className={`p-5 rounded-2xl border-2 cursor-pointer transition-all flex items-center justify-between ${
                    paymentMethod === "card" ? "border-yellow-500 bg-yellow-50/30" : "border-gray-100 bg-gray-50 hover:border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      paymentMethod === "card" ? "border-yellow-500" : "border-gray-300"
                    }`}>
                      {paymentMethod === "card" && <div className="w-3 h-3 rounded-full bg-yellow-500"></div>}
                    </div>
                    <span className="font-bold text-gray-900 text-lg">Credit / Debit Card</span>
                  </div>
                  <span className="text-xl">💳</span>
                </div>
              </div>

              <div className="bg-gray-50 p-6 rounded-2xl border border-gray-100 mt-8">
                <div className="flex justify-between items-center mb-3">
                  <span className="text-gray-500 font-medium">Subtotal</span>
                  <span className="font-bold text-gray-900">Ksh {subtotal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center mb-4 pb-4 border-b border-gray-200">
                  <span className="text-gray-500 font-medium">Shipping</span>
                  <span className="font-semibold text-green-600">Calculated later</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-lg font-bold text-gray-900">Total to Pay</span>
                  <span className="text-2xl font-extrabold text-black">Ksh {subtotal.toLocaleString()}</span>
                </div>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-gray-100 flex flex-col sm:flex-row justify-between items-center gap-4">
              <button
                onClick={handlePrevStep}
                disabled={isProcessing}
                className="w-full sm:w-auto px-6 py-4 text-gray-500 hover:text-gray-900 font-semibold flex items-center justify-center gap-2 transition-colors disabled:opacity-50"
              >
                <ArrowLeft size={20} /> Back to Shipping
              </button>
              <button
                onClick={handleCheckout}
                disabled={isProcessing || (paymentMethod === "mpesa" && !phoneNumber)}
                className="w-full sm:w-auto bg-black hover:bg-gray-900 text-white px-10 py-4 rounded-xl font-bold flex items-center justify-center gap-3 transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-70 disabled:hover:scale-100 shadow-xl shadow-gray-200"
              >
                {isProcessing ? (
                  <>
                    <span className="w-5 h-5 border-2 border-white/20 border-t-white rounded-full animate-spin"></span>
                    Processing Payment...
                  </>
                ) : (
                  paymentMethod === "mpesa" ? `Pay Ksh ${subtotal.toLocaleString()} via M-Pesa` : `Pay Ksh ${subtotal.toLocaleString()}`
                )}
              </button>
            </div>
          </div>
        )}

        {/* Step 4: Success Message */}
        {step === 4 && (
          <div className="text-center py-12 animate-in zoom-in-95 duration-500">
            <div className="w-24 h-24 bg-green-100 text-green-500 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle size={48} />
            </div>
            <h2 className="text-3xl font-extrabold text-gray-900 mb-4">Order Placed Successfully!</h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto text-lg leading-relaxed">
              Thank you for shopping at Pak Fashions. Your order has been received and is being processed. 
              We'll send you an update via SMS shortly.
            </p>
            <div className="bg-gray-50 border border-gray-100 rounded-2xl p-6 max-w-md mx-auto mb-10 text-left">
              <h4 className="font-bold text-gray-900 mb-3 border-b border-gray-200 pb-2">Order Summary</h4>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Transaction Ref:</span>
                <span className="font-semibold text-gray-900">PK-{Math.floor(Math.random() * 1000000)}</span>
              </div>
              <div className="flex justify-between text-sm mb-2">
                <span className="text-gray-500">Shipping to:</span>
                <span className="font-semibold text-gray-900 truncate max-w-[200px]">{shippingAddress}</span>
              </div>
            </div>
            <Link to="/shop">
              <button className="bg-yellow-500 hover:bg-yellow-400 text-black px-10 py-4 rounded-xl font-bold shadow-md hover:-translate-y-1 transition-all">
                Continue Shopping
              </button>
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}

export default Checkout;
